import { Injectable } from '@angular/core';

// A4 in PDF points (1 pt = 1/72 inch)
const A4_SHORT_SIDE_PT = 595.28;
const A4_LONG_SIDE_PT = 841.89;
const PAGE_MARGIN_PT = 28; // ≈ 10 mm
// A4 at 300 dpi — enough for print, keeps phone photos well below the upload limit
const MAX_RASTER_SIDE_PX = 3508;
// SVGs without width/height have no intrinsic size; rendered at this longest side instead
const FALLBACK_SVG_SIDE_PX = 2000;
// Bitmaps are never enlarged beyond their natural size at 96 dpi
const PT_PER_CSS_PX = 0.75;
const JPEG_QUALITY = 0.9;

/**
 * Converts a JPEG, PNG or SVG into a one-page A4 PDF (portrait or landscape to match the image,
 * scaled to fit inside a margin), since the Dokumente tab stores PDFs only. The image is drawn onto
 * a canvas first: that applies the EXIF orientation of phone photos, rasterizes SVGs, and caps the
 * resolution. pdf-lib is loaded on demand so it stays out of the page's bundle.
 */
@Injectable({
  providedIn: 'root',
})
export class ImagePdfConverterService {
  public async toPdf(file: File): Promise<File> {
    const isJpeg = file.type === 'image/jpeg' || /\.jpe?g$/i.test(file.name);
    const isSvg = file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);

    const image = await this.loadImage(file, isSvg);
    const { width, height } = this.rasterSize(image, isSvg);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D context unavailable');
    context.drawImage(image, 0, 0, width, height);
    // PNG keeps transparency and the sharp edges of drawings/SVGs
    const imageBytes = await this.canvasBytes(canvas, isJpeg ? 'image/jpeg' : 'image/png');

    const { PDFDocument } = await import('pdf-lib');
    const pdf = await PDFDocument.create();
    pdf.setTitle(file.name);
    const embedded = isJpeg ? await pdf.embedJpg(imageBytes) : await pdf.embedPng(imageBytes);

    const landscape = width > height;
    const pageWidth = landscape ? A4_LONG_SIDE_PT : A4_SHORT_SIDE_PT;
    const pageHeight = landscape ? A4_SHORT_SIDE_PT : A4_LONG_SIDE_PT;
    // Vector graphics may fill the page; bitmaps would only get blurry
    const naturalScale = isSvg ? Infinity : (this.naturalSize(image, isSvg).width * PT_PER_CSS_PX) / width;
    const scale = Math.min(
      (pageWidth - 2 * PAGE_MARGIN_PT) / width,
      (pageHeight - 2 * PAGE_MARGIN_PT) / height,
      naturalScale
    );
    const drawWidth = width * scale;
    const drawHeight = height * scale;
    pdf.addPage([pageWidth, pageHeight]).drawImage(embedded, {
      x: (pageWidth - drawWidth) / 2,
      y: (pageHeight - drawHeight) / 2,
      width: drawWidth,
      height: drawHeight,
    });

    const pdfBytes = await pdf.save();
    return new File([pdfBytes as Uint8Array<ArrayBuffer>], this.pdfName(file.name), { type: 'application/pdf' });
  }

  private loadImage(file: File, isSvg: boolean): Promise<HTMLImageElement> {
    // Some systems report no MIME type; an SVG is only rendered as image/svg+xml
    const source = isSvg && file.type !== 'image/svg+xml' ? new Blob([file], { type: 'image/svg+xml' }) : file;
    const url = URL.createObjectURL(source);
    const image = new Image();
    return new Promise<HTMLImageElement>((resolve, reject) => {
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error(`Image could not be decoded: ${file.name}`));
      image.src = url;
    }).finally(() => URL.revokeObjectURL(url));
  }

  // Natural size, with the fallback for SVGs that have none
  private naturalSize(image: HTMLImageElement, isSvg: boolean): { width: number; height: number } {
    if (image.naturalWidth > 0 && image.naturalHeight > 0) {
      return { width: image.naturalWidth, height: image.naturalHeight };
    }
    if (!isSvg) throw new Error('Image has no size');
    return { width: FALLBACK_SVG_SIDE_PX, height: FALLBACK_SVG_SIDE_PX };
  }

  // SVGs are rendered at the maximum resolution so they stay sharp; bitmaps are only scaled down
  private rasterSize(image: HTMLImageElement, isSvg: boolean): { width: number; height: number } {
    const natural = this.naturalSize(image, isSvg);
    const longest = Math.max(natural.width, natural.height);
    const factor = isSvg ? MAX_RASTER_SIDE_PX / longest : Math.min(1, MAX_RASTER_SIDE_PX / longest);
    return {
      width: Math.max(1, Math.round(natural.width * factor)),
      height: Math.max(1, Math.round(natural.height * factor)),
    };
  }

  private canvasBytes(canvas: HTMLCanvasElement, type: 'image/jpeg' | 'image/png'): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error('Canvas could not be encoded'));
            return;
          }
          blob.arrayBuffer().then(buffer => resolve(new Uint8Array(buffer)), reject);
        },
        type,
        JPEG_QUALITY
      );
    });
  }

  // "Foto.jpg" → "Foto.pdf" (the backend would otherwise append .pdf to the full name)
  private pdfName(name: string): string {
    const base = name.replace(/\.(jpe?g|png|svg)$/i, '').trim();
    return `${base || 'Bild'}.pdf`;
  }
}
